import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { Alert, Button, Card, CardBody } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { RiFilter3Fill, RiAddLine } from 'react-icons/ri';
import { SortingState } from '@tanstack/react-table';
import { toast, Bounce } from 'react-toastify';
import { Link } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';

import DataTable from '@/common/tableData/DataTable';
import Paginator from '@/pages/common/paginator';
import { convertSortingToApiFormat } from '@/common/utils/convert-sorting-to-api-format';
import useMe from '@/services/auth/queries/useMe';
import { NO_AGENT_ID } from '../AgentProfile/agent-profile';
import {
  fetchAgentsRest,
  createAgentDealRest,
  updateAgentDealRest,
  deactivateAgentDealRest,
  convertFilterToApiFormat,
} from '@/services/agents/agents-service';

import { initialState, queryReducer } from '@/pages/agents/Agents/agents-query-reducer';
import { createColumns } from '@/pages/agents/Agents/agents.columns';
import {
  AdminAgentsResponseDto,
  AdminAgentInfoDto,
  CreateAgentDealDto,
  UpdateAgentDealDto,
  UserRole,
} from '@repo/common-types';
import { AgentsFilter, AgentDealFormModal } from './components';
import StandardLayout from '@/common/layouts/StandardLayout';
import DebouncedInput from '@/components/DebounceInput';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';

const AgentsPage: React.FC = () => {
  const { data: meResponse } = useMe();
  const canViewProfile = meResponse?.role === UserRole.SUPER_ADMIN;

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [queryParams, dispatch] = useReducer(queryReducer, initialState);

  // Modal state management
  const [showAgentDealModal, setShowAgentDealModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AdminAgentInfoDto | undefined>(undefined);

  // Confirmation modal state management
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAgentDealId, setSelectedAgentDealId] = useState<string | undefined>(undefined);
  const { sorting, ...rest } = queryParams;

  const { data, isLoading, error, refetch } = useQuery<AdminAgentsResponseDto>({
    queryKey: ['agentsData', queryParams],
    queryFn: () =>
      fetchAgentsRest({
        skip: rest.skip,
        take: rest.take,
        filter: convertFilterToApiFormat(rest.filter),
        sorts: convertSortingToApiFormat(sorting),
      }),
    staleTime: 60000,
  });

  // Check if filters differ from initial state
  const hasActiveFilters = useMemo(() => {
    const currentFilter = queryParams.filter || {};
    const initialFilter = initialState.filter || {};

    return Object.keys(initialFilter).some((key) => {
      const currentValue = currentFilter[key as keyof typeof currentFilter];
      const initialValue = initialFilter[key as keyof typeof initialFilter];
      return currentValue !== initialValue;
    });
  }, [queryParams.filter]);

  const handleSearch = useCallback((value: string | number) => {
    dispatch({ type: 'SET_SEARCH', payload: value.toString() });
  }, []);

  const handleModalClose = useCallback(() => {
    setShowAgentDealModal(false);
    setSelectedAgent(undefined);
  }, []);

  const handleFormSubmit = useCallback(
    async (formData: CreateAgentDealDto) => {
      // Determine which operation we're attempting
      const isUpdate = selectedAgent?.hasAgentDeal && selectedAgent?.agentDeal?.id;

      try {
        if (isUpdate) {
          // Update existing agent deal
          const updateData: UpdateAgentDealDto = {
            dealType: formData.dealType,
            rakeSharePercentage: formData.rakeSharePercentage,
            flatFee: formData.flatFee,
            bonus: formData.bonus,
            isRegionalManager: formData.isRegionalManager,
            regionId: formData.regionId,
            automaticDownlineInheritance: formData.automaticDownlineInheritance,
            completeUserVisibility: formData.completeUserVisibility,
            startDate: formData.startDate,
            endDate: formData.endDate,
          };

          await updateAgentDealRest(selectedAgent.agentDeal!.id, updateData);
        } else {
          // Create new agent deal
          await createAgentDealRest(formData);
        }

        // Refetch data to update the table
        refetch();

        // Show success message
        const action = isUpdate ? 'updated' : 'created';
        toast.success(`Agent deal ${action} successfully!`, {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'dark',
          transition: Bounce,
        });

        handleModalClose();
      } catch (error) {
        console.error('Error submitting agent deal:', error);

        // Extract specific error message and create title
        const specificError = error instanceof Error ? error.message : '';
        const action = isUpdate ? 'update' : 'create';
        const title = `Failed to ${action} agent deal.`;

        // Format message with title and specific error details
        const errorMessage = specificError ? specificError : 'Please try again.';

        toast.error(
          <div className="d-flex flex-column gap-2">
            <span className="fw-bold">{title}</span>
            <span>{errorMessage}</span>
          </div>,
          {
            position: 'top-right',
            autoClose: 8000, // Longer duration for potentially longer error messages
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'dark',
            transition: Bounce,
          },
        );
      }
    },
    [selectedAgent, handleModalClose, refetch],
  );

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      dispatch({ type: 'SET_SORTING', payload: newSorting });
    },
    [sorting],
  );

  const handleCreateAgentDeal = useCallback(
    (userId: string) => {
      const agent = data?.agents?.find((a) => a.id === userId);
      if (agent) {
        setSelectedAgent(agent);
        setShowAgentDealModal(true);
      }
    },
    [data?.agents],
  );

  const handleEditDeal = useCallback(
    (agentDealId: string) => {
      const agent = data?.agents?.find((a) => a.agentDeal?.id === agentDealId);
      if (agent) {
        setSelectedAgent(agent);
        setShowAgentDealModal(true);
      }
    },
    [data?.agents],
  );

  const handleDeactivateDeal = (agentDealId: string) => {
    setSelectedAgentDealId(agentDealId);
    setShowConfirmModal(true);
  };

  const handleConfirmDeactivateDeal = useCallback(async () => {
    try {
      if (!selectedAgentDealId) throw new Error('No agent deal selected');

      await deactivateAgentDealRest(selectedAgentDealId);

      // Refetch data to update the table
      refetch();

      toast.success('Agent deal deactivated successfully!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
        transition: Bounce,
      });
    } catch (error) {
      console.error('Error deactivating agent deal:', error);

      // Extract specific error message and create title
      const specificError = error instanceof Error ? error.message : '';
      const title = 'Failed to deactivate agent deal.';

      // Format message with title and specific error details
      const errorMessage = specificError ? specificError : 'Please try again.';

      toast.error(
        <div className="d-flex flex-column gap-2">
          <span className="fw-bold">{title}</span>
          <span>{errorMessage}</span>
        </div>,
        {
          position: 'top-right',
          autoClose: 8000, // Longer duration for potentially longer error messages
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'dark',
          transition: Bounce,
        },
      );
    } finally {
      setShowConfirmModal(false);
      setSelectedAgentDealId(undefined);
    }
  }, [refetch, selectedAgentDealId]);

  const handlePaginationChange = useCallback((skip: number, take: number) => {
    dispatch({ type: 'SET_PAGINATION', payload: { skip, take } });
  }, []);

  const columns = useMemo(
    () => createColumns(canViewProfile, handleCreateAgentDeal, handleEditDeal, handleDeactivateDeal),
    [canViewProfile, handleCreateAgentDeal, handleEditDeal, handleDeactivateDeal],
  );

  return (
    <StandardLayout
      pageTitle="Agents Management"
      headerButton={
        <Button
          color="primary"
          className="d-flex align-items-center gap-2"
          onClick={() => {
            setSelectedAgent(undefined);
            setShowAgentDealModal(true);
          }}
        >
          <RiAddLine />
          Create Agent Deal
        </Button>
      }
    >
      {canViewProfile && (
        <Card className="mb-3 border-dashed" style={{ borderStyle: 'dashed' }}>
          <CardBody className="py-2">
            <Link to={`/agents/${NO_AGENT_ID}`} className="d-flex align-items-center text-decoration-none gap-2">
              <span className="fw-medium">No Agent</span>
              <span className="text-muted">— View and assign unassigned players</span>
            </Link>
          </CardBody>
        </Card>
      )}

      {error ? (
        <Alert color="danger">Error when fetching agents data</Alert>
      ) : (
        <>
          <div className="w-100 d-flex justify-content-between align-items-center mb-4 gap-2">
            <div className="d-flex align-items-center justify-content-start gap-2">
              <DebouncedInput className="form-control w-auto" placeholder="Search" onChange={handleSearch} />
              <Button type="button" outline color="dark" onClick={() => setShowFilterModal(true)}>
                <span className="d-flex align-items-center gap-2">
                  <RiFilter3Fill size={20} />
                  Filter
                </span>
              </Button>
              {hasActiveFilters && (
                <Button type="button" outline color="dark" onClick={() => dispatch({ type: 'RESET_FILTERS' })}>
                  Clear Filters
                </Button>
              )}
            </div>
          </div>

          <DataTable
            data={data?.agents || []}
            columns={columns}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            enableSorting={true}
          />

          <Paginator
            take={queryParams.take || 500}
            skip={queryParams.skip || 0}
            showing={data?.agents?.length || 0}
            totalCount={data?.totalCount || 0}
            setTake={(take) => handlePaginationChange(queryParams.skip || 0, take)}
            setSkip={(skip) => handlePaginationChange(skip, queryParams.take || 500)}
          />
        </>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <AgentsFilter
          onClose={() => setShowFilterModal(false)}
          onApplyFilters={(filters) => {
            dispatch({ type: 'SET_FILTERS', payload: filters });
            setShowFilterModal(false);
          }}
          currentFilters={queryParams.filter}
        />
      )}

      {/* Agent Deal Form Modal */}
      {showAgentDealModal && (
        <AgentDealFormModal
          onClose={handleModalClose}
          onSubmit={handleFormSubmit}
          agent={selectedAgent} // Can be undefined for create mode
        />
      )}

      {showConfirmModal && (
        <ConfirmModal
          onClose={() => {
            setShowConfirmModal(false);
            setSelectedAgentDealId(undefined);
          }}
          onConfirm={handleConfirmDeactivateDeal}
          title="Confirm"
          message="Are you sure you want to deactivate this agent deal?"
        />
      )}
    </StandardLayout>
  );
};

export default AgentsPage;
